import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { SlidersHorizontal } from 'lucide-react';

import DataTable from '../../../components/ui/DataTable';
import Button from '../../../components/ui/Button';
import OptionsMenu from '../../../components/ui/OptionsMenu';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import LoadErrorBanner from '../../../components/ui/LoadErrorBanner';
import ReminderDrawer from '../../performance-reminders/ReminderDrawer';
import ExemptionDrawer, { canExempt, canRemoveExemption } from '../../../components/performance/ExemptionDrawer';
import { Badge, Sub } from '../../performance-operations/styles';
import api from '../../../services/api';
import { formatDate } from '../../../utils/Utils';
import TaskFilterDrawer from '../components/TaskFilterDrawer';
import TaskDetailDrawer from '../components/TaskDetailDrawer';
import {
  PAGE_SIZE, SORT_KEYS, assigneeText, canRemind, exportTasks, fetchTasks, lastAction, stageLabel, statusLabel, taskLabel, tasksParams,
} from '../config/tasks';

// Spec §7.3 — Görevler: tam boy DataTable (EmployeesPage aynası; başlık yalnız tabloda; tek kaydırma). Süzgeçler URL'de (§19/1):
// ortak şerit + "Görev Süzgeçleri" çekmecesi; tablonun kendi süzgeç diyaloğu kapalı. Liste her zaman yalnız zorunlu görevler (§19/2).
// Satır: Detay, Hatırlat, Muaf Tut / Muafiyeti Kaldır. Çoklu seçim (yalnız mevcut sayfa): Hatırlat (N), Muaf Tut (N).
// İndirme simgesi sunucunun Excel dökümünü (bütün sayfalar, aynı süzgeçler) indirir (§19/8).
const TABLE_CHROME = 150;   // araç çubuğu + başlık satırı + sayfalama
const BANNER_HEIGHT = 52;   // yüklenemedi bandı (tablo sayfalaması kırpılmasın)

export default function TasksTab({
  filters, taskFilters, onTaskFiltersChange, height,
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const filterKey = JSON.stringify([filters, taskFilters]);
  // Sayfa/sıra süzgeç anahtarına bağlıdır: süzgeç değişince sayfa 1'e döner (çift istek atmadan).
  const [paging, setPaging] = useState({ key: filterKey, pageNumber: 1, pageSize: PAGE_SIZE, sort: null, order: null });
  const pageNumber = paging.key === filterKey ? paging.pageNumber : 1;
  const [refresh, setRefresh] = useState(0);
  const [state, setState] = useState({ fetching: true, rows: [], total: 0, error: false });
  const [filterOpen, setFilterOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [detailReload, setDetailReload] = useState(0);
  const [reminder, setReminder] = useState(null);
  const [exempt, setExempt] = useState(null);         // { task } | { tasks }
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removing, setRemoving] = useState(false);

  const params = useMemo(
    () => tasksParams(filters, taskFilters, { pageNumber, pageSize: paging.pageSize, sort: paging.sort, order: paging.order }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterKey, pageNumber, paging.pageSize, paging.sort, paging.order],
  );
  const paramsKey = JSON.stringify(params);
  const seq = useRef(0);
  useEffect(() => {
    if (!filters.periodId) return;
    const mine = ++seq.current;
    setState((s) => ({ ...s, fetching: true, error: false }));
    fetchTasks(params)
      .then((r) => { if (mine === seq.current) setState({ fetching: false, rows: r.rows, total: r.total, error: false }); })
      .catch(() => { if (mine === seq.current) setState({ fetching: false, rows: [], total: 0, error: true }); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey, refresh]);

  const changed = useCallback(() => {
    setRefresh((r) => r + 1);
    setDetailReload((r) => r + 1);
  }, []);

  const onTableChanged = useCallback((action, ts) => {
    const sortName = ts.sortOrder?.name;
    const sort = sortName ? SORT_KEYS[sortName] || null : null;
    setPaging({
      key: filterKey,
      pageNumber: action === 'sort' ? 1 : (ts.page || 0) + 1,
      pageSize: ts.rowsPerPage || PAGE_SIZE,
      sort,
      order: sort ? (ts.sortOrder.direction === 'desc' ? 'desc' : 'asc') : null,
    });
  }, [filterKey]);

  // Yalnız hatırlatılabilir görevler gider; çekmece geri kalanı zaten sayardı ama boş bir önizleme açmak anlamsız.
  const remind = useCallback((rows) => setReminder({ periodId: filters.periodId, taskIds: rows.filter(canRemind).map((r) => r.taskId) }), [filters.periodId]);

  const rowActions = useCallback((row) => [
    { type: 'detail', label: t('performance.operations.rowAction.detail'), onClick: () => setDetailId(row.taskId) },
    ...(canRemind(row) ? [{ type: 'send', label: t('performance.operations.rowAction.remind'), onClick: () => remind([row]) }] : []),
    ...(canExempt(row) ? [{ type: 'waive', label: t('performance.operations.rowAction.exempt'), onClick: () => setExempt({ task: row }) }] : []),
    ...(canRemoveExemption(row)
      ? [{ type: 'return_to_draft', label: t('performance.processHealth.rowAction.removeExemption'), onClick: () => setRemoveTarget(row) }]
      : []),
  ], [t, remind]);

  const rows = state.rows;
  const columns = useMemo(() => {
    const col = (name, labelKey, render, sort = false) => ({
      name,
      label: t(labelKey),
      options: { filter: false, sort, customBodyRender: (v, meta) => render(rows[meta.rowIndex], v) },
    });
    return [
      col('employeeName', 'performance.operations.col.employee', (r) => r?.employeeName || '-', true),
      col('stageKey', 'performance.operations.col.stage', (r) => (r ? stageLabel(t, r.stageKey) : '-')),
      col('taskKey', 'performance.operations.col.task', (r) => (r ? taskLabel(t, r.taskKey) : '-')),
      col('assigneeName', 'performance.operations.col.assignee', (r) => (r ? (
        <>
          <div>{assigneeText(t, r)}</div>
          {r.assigneeTitle ? <Sub>{r.assigneeTitle}</Sub> : null}
        </>
      ) : '-'), true),
      col('dueAt', 'performance.operations.col.due', (r) => (r?.dueAt ? formatDate(r.dueAt, lang) : '-'), true),
      col('status', 'performance.operations.col.status', (r) => (r ? <Badge data-status={r.status}>{statusLabel(t, r.status)}</Badge> : '-')),
      col('overdueDays', 'performance.operations.col.overdueDays', (r) => (r?.overdueDays
        ? <Badge $tone="danger">{t('performance.operations.days', { count: r.overdueDays })}</Badge>
        : '-'), true),
      col('lastAction', 'performance.processHealth.col.lastAction', (r) => {
        const a = lastAction(r);
        return a ? t(`performance.processHealth.lastAction.${a.kind}`, { date: formatDate(a.at, lang) }) : '-';
      }),
      {
        name: 'rowActions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, meta) => {
            const r = rows[meta.rowIndex];
            return r ? <OptionsMenu row={{ ...r, id: r.taskId }} actions={rowActions(r)} /> : null;
          },
        },
      },
    ];
  }, [t, lang, rows, rowActions]);

  const selectionActions = useMemo(() => [
    {
      key: 'remind',
      label: (sel) => t('performance.processHealth.selection.remind', { count: sel.filter(canRemind).length }),
      disabled: (sel) => !sel.some(canRemind),
      onClick: (sel) => remind(sel),
    },
    {
      key: 'exempt',
      label: (sel) => t('performance.processHealth.selection.exempt', { count: sel.filter(canExempt).length }),
      disabled: (sel) => !sel.some(canExempt),
      onClick: (sel) => setExempt({ tasks: sel }),
    },
  ], [t, remind]);

  const removeExemption = useCallback(async () => {
    if (!removeTarget) return;
    setRemoving(true);
    try {
      await api.delete(`/v1/period-stage-tasks/${removeTarget.taskId}/exemption`);
      changed();
    } catch {
      // Hata tostu yakalayıcıda.
    } finally {
      setRemoving(false);
      setRemoveTarget(null);
    }
  }, [removeTarget, changed]);

  return (
    <div data-tab-content="tasks">
      {state.error ? <LoadErrorBanner message={t('performance.operations.panel.error')} /> : null}
      <DataTable
        title={t('performance.processHealth.tasksTitle')}
        fetching={state.fetching}
        columns={columns}
        data={rows}
        customIdSelector="taskId"
        totalRowCount={state.total}
        pageIndex={pageNumber - 1}
        pageSize={paging.pageSize}
        onTableChanged={onTableChanged}
        disableFilters
        tableBodyMaxHeight={height ? `${Math.max(200, height - TABLE_CHROME - (state.error ? BANNER_HEIGHT : 0))}px` : undefined}
        selectionActions={selectionActions}
        onDownload={() => { exportTasks(params); return false; }}
        toolbarActions={(
          <Button variant="outline" onClick={() => setFilterOpen(true)} data-action="task-filters">
            <SlidersHorizontal size={16} />
            {' '}
            {t('performance.processHealth.filter.open')}
          </Button>
        )}
      />
      <TaskFilterDrawer
        open={filterOpen}
        value={taskFilters}
        onClose={() => setFilterOpen(false)}
        onApply={(next) => { setFilterOpen(false); onTaskFiltersChange(next); }}
      />
      <TaskDetailDrawer
        taskId={detailId}
        open={!!detailId}
        reloadKey={detailReload}
        onClose={() => setDetailId(null)}
        onRemind={(r) => remind([r])}
        onExempt={(r) => setExempt({ task: r })}
        onRemoveExemption={(r) => setRemoveTarget(r)}
      />
      <ReminderDrawer
        open={!!reminder}
        selection={reminder}
        onClose={() => setReminder(null)}
        onSent={() => { setReminder(null); changed(); }}
      />
      <ExemptionDrawer
        open={!!exempt}
        task={exempt?.task}
        tasks={exempt?.tasks}
        onClose={() => setExempt(null)}
        onDone={() => { setExempt(null); changed(); }}
      />
      <ConfirmDialog
        isOpen={!!removeTarget}
        title={t('performance.processHealth.removeExemption.title')}
        message={t('performance.processHealth.removeExemption.message')}
        confirmText={t('performance.processHealth.rowAction.removeExemption')}
        onConfirm={removeExemption}
        onCancel={() => setRemoveTarget(null)}
        loading={removing}
        type="warning"
      />
    </div>
  );
}
